import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, ChevronLeft, ChevronRight, Filter, X } from 'lucide-react';
import Header from '../components/Header';
// ❌ SUPPRIMÉ : import { initialEvents } from '../data/events';
// ✅ AJOUTÉ : import du service API
import { festivalsService } from '../services/festivalsService';
import circuitsService from '../services/circuitsService';
import { isHoliday } from '../data/holidays';
import { getDaysInMonth, monthNames, dayNames } from '../utils/dateUtils';
import {
  getSubEventsForDate,
  getActiveEventsForDate,
  getEventsForMonth,
  formatEventPeriod
} from '../utils/eventUtils';

// État du calendrier (mois affiché, jour sélectionné, filtres, défilement)
// conservé le temps de la session de l'onglet : en revenant d'une fiche de
// tournoi (bouton retour ou fil d'Ariane), on retrouve le calendrier tel
// qu'on l'avait laissé. Le stockage peut être indisponible : tout est protégé.
const CALENDAR_STATE_KEY = 'moker.calendar.state.v1';

const loadCalendarState = () => {
  try {
    const raw = sessionStorage.getItem(CALENDAR_STATE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
};

const saveCalendarState = (patch) => {
  try {
    sessionStorage.setItem(
      CALENDAR_STATE_KEY,
      JSON.stringify({ ...loadCalendarState(), ...patch })
    );
  } catch (e) {
    /* stockage indisponible : on ignore */
  }
};

const CalendarPage = () => {
  const navigate = useNavigate();
  // Lu une seule fois, au premier rendu
  const [saved] = useState(loadCalendarState);
  const [currentDate, setCurrentDate] = useState(() =>
    Number.isInteger(saved.year) && Number.isInteger(saved.month)
      ? new Date(saved.year, saved.month, 1)
      : new Date()
  );
  const [selectedDate, setSelectedDate] = useState(() => {
    if (!saved.selectedDate) return null;
    const d = new Date(`${saved.selectedDate}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  });

  // ✅ MODIFIÉ : Charger depuis l'API au lieu du mock
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ✅ AJOUTÉ : État pour les circuits
  const [circuits, setCircuits] = useState([]);
  const [selectedCircuits, setSelectedCircuits] = useState(() => saved.selectedCircuits || []); // [] = tous les circuits (sélection multiple)

  // Nouveaux états pour les filtres
  const [selectedFestivals, setSelectedFestivals] = useState(() => saved.selectedFestivals || []);
  const [showFilters, setShowFilters] = useState(() => !!saved.showFilters);
  const [filterParticipating, setFilterParticipating] = useState(() => saved.filterParticipating || 'all'); // all, participating, not-participating

  // ✅ AJOUTÉ : Charger les festivals depuis l'API au démarrage
  useEffect(() => {
    loadFestivals();
    loadCircuits();
  }, []);

  // Mémorise l'état du calendrier à chaque changement
  useEffect(() => {
    const d = selectedDate;
    saveCalendarState({
      year: currentDate.getFullYear(),
      month: currentDate.getMonth(),
      selectedDate: d
        ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        : null,
      selectedCircuits,
      selectedFestivals,
      filterParticipating,
      showFilters,
    });
  }, [currentDate, selectedDate, selectedCircuits, selectedFestivals, filterParticipating, showFilters]);

  // Mémorise la position de défilement (écouteur passif, une écriture par image)
  useEffect(() => {
    let frame = null;
    const onScroll = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        saveCalendarState({ scrollY: window.scrollY });
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, []);

  // Restaure la position de défilement une fois le contenu chargé
  useEffect(() => {
    if (!loading && saved.scrollY > 0) {
      window.scrollTo(0, saved.scrollY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const loadCircuits = async () => {
    try {
      const circuitsData = await circuitsService.getAll();
      setCircuits(circuitsData);
      console.log('✅ Circuits chargés depuis l\'API :', circuitsData.length, 'circuits');
    } catch (err) {
      console.error('❌ Erreur lors du chargement des circuits:', err);
    }
  };

  const loadFestivals = async () => {
    try {
      setLoading(true);
      setError(null);

      // Appeler l'API pour récupérer tous les festivals avec leurs tournois
      const festivalsData = await festivalsService.getAll();

      // Adapter le format si nécessaire (l'API retourne déjà le bon format)
      setEvents(festivalsData);

      console.log('✅ Festivals chargés depuis l\'API :', festivalsData.length, 'festivals');
    } catch (err) {
      console.error('❌ Erreur lors du chargement des festivals:', err);
      setError('Impossible de charger les festivals. Vérifiez que l\'API est démarrée.');
    } finally {
      setLoading(false);
    }
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const handleSubEventClick = (subEvent) => {
    navigate(`/event/${subEvent.parentEvent.id}/subevent/${subEvent.id}`);
  };

  // Gestion des filtres de festivals
  const toggleFestivalFilter = (festivalId) => {
    setSelectedFestivals(prev => {
      if (prev.includes(festivalId)) {
        return prev.filter(id => id !== festivalId);
      } else {
        return [...prev, festivalId];
      }
    });
  };

  // ✅ AJOUTÉ : Gestion des filtres de circuits (sélection multiple)
  const toggleCircuitFilter = (circuitId) => {
    setSelectedCircuits(prev => {
      if (prev.includes(circuitId)) {
        return prev.filter(id => id !== circuitId);
      } else {
        return [...prev, circuitId];
      }
    });
  };

  const clearAllFilters = () => {
    setSelectedFestivals([]);
    setFilterParticipating('all');
    setSelectedCircuits([]); // ✅ MODIFIÉ : Reset la liste de circuits (array vide)
  };

  const hasActiveFilters = selectedFestivals.length > 0 || filterParticipating !== 'all' || selectedCircuits.length > 0; // ✅ MODIFIÉ : Vérifier array length

  // Filtrer les événements
  const filteredEvents = events.filter(event => {
    // ✅ MODIFIÉ : Filtre par circuits (sélection multiple)
    const matchCircuit = selectedCircuits.length === 0 || selectedCircuits.includes(event.circuit?.id);

    // Filtre par festival sélectionné
    const matchFestival = selectedFestivals.length === 0 || selectedFestivals.includes(event.id);

    // Filtre par participation
    const matchParticipating = filterParticipating === 'all' ||
                              (filterParticipating === 'participating' && event.participating) ||
                              (filterParticipating === 'not-participating' && !event.participating);

    return matchCircuit && matchFestival && matchParticipating; // ✅ AJOUTÉ : matchCircuit
  });

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate);
  const calendarDays = [];

  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(null);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  // ✅ AJOUTÉ : Affichage de l'état de chargement
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg p-12 text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-6"></div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Chargement du calendrier</h2>
              <p className="text-gray-600">Récupération des festivals depuis l'API...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ✅ AJOUTÉ : Affichage de l'erreur
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">
            <div className="bg-red-50 border-2 border-red-200 rounded-lg shadow-lg p-8">
              <div className="flex items-center mb-4">
                <span className="text-4xl mr-4">⚠️</span>
                <h2 className="text-2xl font-bold text-red-800">Erreur de chargement</h2>
              </div>
              <p className="text-red-700 mb-6 text-lg">{error}</p>
              <div className="space-y-3 bg-red-100 border border-red-300 rounded-lg p-4 mb-6">
                <p className="text-sm text-red-800 font-semibold">Vérifications à faire :</p>
                <ul className="list-disc list-inside text-sm text-red-700 space-y-1 ml-2">
                  <li>Le backend Spring Boot est démarré (<code>mvn spring-boot:run</code>)</li>
                  <li>L'API est accessible sur <code>http://localhost:8080/api</code></li>
                  <li>Vous êtes connecté (token JWT valide)</li>
                  <li>Le fichier <code>.env</code> contient <code>REACT_APP_USE_MOCK_API=false</code></li>
                </ul>
              </div>
              <button
                onClick={loadFestivals}
                className="bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition flex items-center gap-2 mx-auto"
              >
                <span>🔄</span> Réessayer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ✅ AJOUTÉ : Message si base de données vide
  if (events.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">
            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg shadow-lg p-8">
              <div className="flex items-center mb-4">
                <span className="text-4xl mr-4">📅</span>
                <h2 className="text-2xl font-bold text-yellow-800">Aucun festival disponible</h2>
              </div>
              <p className="text-yellow-700 mb-6 text-lg">
                La base de données ne contient aucun festival pour le moment.
              </p>
              <div className="space-y-3 bg-yellow-100 border border-yellow-300 rounded-lg p-4 mb-6">
                <p className="text-sm text-yellow-800 font-semibold">💡 Pour peupler la base de données :</p>
                <ol className="list-decimal list-inside text-sm text-yellow-700 space-y-2 ml-2">
                  <li>Exécutez le script SQL : <code className="bg-yellow-200 px-2 py-1 rounded">mysql -u root -p poker_festivals &lt; seed-data.sql</code></li>
                  <li>Ou créez des festivals via l'interface admin : <a href="/manage-festivals" className="text-blue-600 underline hover:text-blue-800">Gestion des festivals</a></li>
                </ol>
              </div>
              <button
                onClick={loadFestivals}
                className="bg-yellow-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-yellow-700 transition flex items-center gap-2 mx-auto"
              >
                <span>🔄</span> Actualiser
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-6">
            {/* Calendar Header with Filters */}
            <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={previousMonth}
                  className="p-2 hover:bg-gray-100 rounded-lg transition"
                >
                  <ChevronLeft size={24} />
                </button>
                <h2 className="text-2xl font-bold">
                  {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                </h2>
                <button
                  onClick={nextMonth}
                  className="p-2 hover:bg-gray-100 rounded-lg transition"
                >
                  <ChevronRight size={24} />
                </button>
              </div>

              <div className="flex items-center gap-2">
                {hasActiveFilters && (
                  <button
                    onClick={clearAllFilters}
                    className="bg-red-100 text-red-700 px-4 py-2 rounded-lg font-semibold hover:bg-red-200 transition flex items-center gap-2"
                  >
                    <X size={18} />
                    Effacer les filtres
                  </button>
                )}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`${showFilters ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'} px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 hover:text-white transition flex items-center gap-2`}
                >
                  <Filter size={18} />
                  Filtres
                  {hasActiveFilters && (
                    <span className="bg-white text-blue-600 px-2 py-0.5 rounded-full text-xs font-bold">
                      {(selectedFestivals.length > 0 ? selectedFestivals.length : 0) + (filterParticipating !== 'all' ? 1 : 0) + (selectedCircuits.length > 0 ? selectedCircuits.length : 0)}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Filters Panel */}
            {showFilters && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Filtrer les événements</h3>

                <div className="space-y-6">
                  {/* ✅ MODIFIÉ : Filtre par circuits (sélection multiple comme festivals) */}
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-2 block">
                      Circuits
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {circuits.map(circuit => (
                        <button
                          key={circuit.id}
                          onClick={() => toggleCircuitFilter(circuit.id)}
                          className={`p-3 rounded-lg font-semibold transition text-left ${
                            selectedCircuits.includes(circuit.id)
                              ? `${circuit.color} text-white`
                              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="flex-1">{circuit.acronym}</span>
                            {selectedCircuits.includes(circuit.id) && (
                              <span className="text-xs px-2 py-1 rounded bg-white bg-opacity-90">
                                ✓
                              </span>
                            )}
                          </div>
                          <div className="text-xs mt-1 opacity-80">
                            {circuit.title}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Filtre par participation */}
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-2 block">
                      Participation
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => setFilterParticipating('all')}
                        className={`px-4 py-2 rounded-lg font-semibold transition ${
                          filterParticipating === 'all'
                            ? 'bg-blue-600 text-white'
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        Tous
                      </button>
                      <button
                        onClick={() => setFilterParticipating('participating')}
                        className={`px-4 py-2 rounded-lg font-semibold transition ${
                          filterParticipating === 'participating'
                            ? 'bg-green-600 text-white'
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        Je participe
                      </button>
                      <button
                        onClick={() => setFilterParticipating('not-participating')}
                        className={`px-4 py-2 rounded-lg font-semibold transition ${
                          filterParticipating === 'not-participating'
                            ? 'bg-gray-600 text-white'
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        Je ne participe pas
                      </button>
                    </div>
                  </div>

                  {/* Filtre par festivals */}
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-2 block">
                      Festivals
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {events.map(event => (
                        <button
                          key={event.id}
                          onClick={() => toggleFestivalFilter(event.id)}
                          className={`p-3 rounded-lg font-semibold transition text-left ${
                            selectedFestivals.includes(event.id)
                              ? 'bg-blue-600 text-white'
                              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${event.color}`}></div>
                            <span className="flex-1">{event.title}</span>
                            {event.participating && (
                              <span className={`text-xs px-2 py-1 rounded ${
                                selectedFestivals.includes(event.id) ? 'bg-white text-blue-600' : 'bg-green-100 text-green-800'
                              }`}>
                                ✓
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Calendar Grid */}
            <div className="mb-6">
              <div className="grid grid-cols-7 gap-2 mb-2">
                {dayNames.map(day => (
                  <div key={day} className="text-center font-semibold text-gray-600 py-2">
                    {day}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-2">
                {calendarDays.map((day, index) => {
                  if (day === null) {
                    return <div key={`empty-${index}`} className="aspect-square"></div>;
                  }

                  const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
                  const isToday = date.toDateString() === new Date().toDateString();
                  const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
                  const holiday = isHoliday(date);
                  const activeEvents = getActiveEventsForDate(filteredEvents, date);
                  const hasEvents = activeEvents.length > 0;

                  return (
                    <button
                      key={day}
                      onClick={() => setSelectedDate(date)}
                      className={`relative aspect-square p-1 sm:p-2 rounded-lg border transition ${
                        isToday
                          ? 'bg-blue-50 border-blue-500 font-bold'
                          : isSelected
                          ? 'bg-blue-100 border-blue-500'
                          : holiday
                          ? 'bg-red-50 border-red-200'
                          : hasEvents
                          ? 'bg-gray-50 border-gray-300 hover:bg-gray-100'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex flex-col h-full">
                        <div className={`text-sm ${isToday ? 'text-blue-600' : 'text-gray-700'}`}>
                          {day}
                        </div>
                        {holiday && (
                          <div className="text-xs text-red-600 font-medium mt-1 hidden sm:block">🎉 {holiday}</div>
                        )}
                        {activeEvents.some(e => e.participating) && (
                          <span
                            className="absolute top-0.5 right-1 text-green-600 font-bold sm:hidden"
                            style={{ fontSize: '0.65rem', lineHeight: '1' }}
                            title="Vous participez"
                          >
                            ✓
                          </span>
                        )}
                        {hasEvents && (
                          <div className="absolute inset-x-0 bottom-1.5 flex flex-nowrap justify-center items-center gap-0.5 px-0.5 sm:hidden">
                            {activeEvents.slice(0, 3).map(event => (
                              <span
                                key={event.id}
                                className={`${event.circuit?.color || 'bg-blue-600'} w-1.5 h-1.5 rounded-full flex-shrink-0`}
                                title={`${event.circuit?.acronym || ''} ${event.title}${event.participating ? ' (Vous participez)' : ''}`}
                              />
                            ))}
                            {activeEvents.length > 3 && (
                              <span className="text-gray-600 font-semibold whitespace-nowrap" style={{ fontSize: '0.55rem', lineHeight: '1' }}>
                                +{activeEvents.length - 3}
                              </span>
                            )}
                          </div>
                        )}
                        {hasEvents && (
                          <div className="space-y-1 mt-1 hidden sm:block">
                            {activeEvents.slice(0, 3).map(event => (
                              <div
                                key={event.id}
                                className={`${event.circuit?.color || 'bg-blue-600'} text-white text-xs px-1 py-0.5 rounded truncate flex items-center justify-between`}
                                title={`${event.circuit?.acronym || ''} ${event.title}${event.participating ? ' (Vous participez)' : ''}`}
                                style={{ fontSize: '0.65rem', lineHeight: '1' }}
                              >
                                <span className="truncate">{event.title}</span>
                                {event.participating && (
                                  <span className="ml-1">✓</span>
                                )}
                              </div>
                            ))}
                            {activeEvents.length > 3 && (
                              <div className="text-xs text-gray-600 font-semibold text-center bg-gray-200 rounded py-0.5" style={{ fontSize: '0.65rem' }}>
                                +{activeEvents.length - 3}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Event Details */}
            <div>
              {selectedDate ? (
                <div>
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <Calendar className="mr-2" size={24} />
                    {selectedDate.toLocaleDateString('fr-FR', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </h3>
                  <div className="space-y-4">
                    {getActiveEventsForDate(filteredEvents, selectedDate).map(event => {
                      // ✅ CORRECTION : Utiliser l'heure locale au lieu de UTC
                      const selectedDateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
                      const eventSubEventsForDay = event.subEvents.filter(
                        subEvent => subEvent.date === selectedDateStr
                      );

                      return (
                        <div
                          key={event.id}
                          className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition"
                        >
                          <div className={`${event.circuit?.color || 'bg-blue-600'} bg-opacity-10 border-l-4 ${(event.circuit?.color || 'bg-blue-600').replace('bg-', 'border-')} p-4`}>
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-3 flex-1">
                                {event.circuit?.logoUrl && (
                                  <img src={event.circuit.logoUrl} alt="Logo circuit" className="w-10 h-10 object-contain" />
                                )}
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    {event.circuit?.acronym && (
                                      <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                        {event.circuit.acronym}
                                      </span>
                                    )}
                                  </div>
                                  <h4 className="font-bold text-lg text-gray-800">{event.title}</h4>
                                  <p className="text-sm text-gray-600">{formatEventPeriod(event)}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {event.participating && (
                                  <span className="bg-green-500 text-white text-xs font-semibold px-2 py-1 rounded-full">
                                    ✓ Inscrit
                                  </span>
                                )}
                                <div className={`w-3 h-3 rounded-full ${event.circuit?.color || 'bg-blue-600'}`}></div>
                              </div>
                            </div>
                          </div>

                          {eventSubEventsForDay.length > 0 && (
                            <div className="p-4">
                              <p className="text-xs font-semibold text-gray-600 mb-3">
                                {eventSubEventsForDay.length} séance{eventSubEventsForDay.length > 1 ? 's' : ''} ce jour :
                              </p>
                              <div className="space-y-3">
                                {eventSubEventsForDay.map(subEvent => (
                                  <div
                                    key={subEvent.id}
                                    onClick={() => handleSubEventClick({ ...subEvent, parentEvent: event })}
                                    className={`bg-gray-50 rounded-lg p-3 border-l-4 transition cursor-pointer hover:shadow-md ${
                                      subEvent.participating ? 'border-green-500 bg-green-50 hover:bg-green-100' : 'border-gray-300 hover:bg-gray-100'
                                    }`}
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className={`text-xs px-2 py-0.5 rounded ${event.color} text-white font-semibold`}>
                                            {subEvent.time}
                                          </span>
                                        </div>
                                        <h5 className="font-semibold text-gray-800">{subEvent.title}</h5>
                                        <p className="text-xs text-gray-600 mt-1">{subEvent.description}</p>
                                        {subEvent.detailedInfo?.location && (
                                          <p className="text-xs text-gray-500 mt-1">📍 {subEvent.detailedInfo.location}</p>
                                        )}
                                      </div>
                                      <div className="ml-2 flex-shrink-0">
                                        {subEvent.participating ? (
                                          <span className="bg-green-500 text-white text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap">
                                            ✓ Inscrit
                                          </span>
                                        ) : (
                                          <span className="bg-gray-300 text-gray-700 text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap">
                                            Absent
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {getActiveEventsForDate(filteredEvents, selectedDate).length === 0 && (
                      <p className="text-gray-500 text-center py-8">Aucun événement ce jour avec les filtres actifs</p>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <Calendar className="mr-2" size={24} />
                    Événements du mois
                    {hasActiveFilters && (
                      <span className="ml-3 text-sm font-normal text-gray-600">
                        ({filteredEvents.length} festival{filteredEvents.length > 1 ? 's' : ''} affiché{filteredEvents.length > 1 ? 's' : ''})
                      </span>
                    )}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">Cliquez sur un jour pour voir les séances</p>
                  <div className="space-y-4">
                    {getEventsForMonth(filteredEvents, currentDate.getFullYear(), currentDate.getMonth())
                      .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
                      .map(event => (
                        <div
                          key={event.id}
                          className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition"
                        >
                          <div className={`${event.circuit?.color || 'bg-blue-600'} bg-opacity-10 border-l-4 ${(event.circuit?.color || 'bg-blue-600').replace('bg-', 'border-')} p-4`}>
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-3 flex-1">
                                {event.circuit?.logoUrl && (
                                  <img src={event.circuit.logoUrl} alt="Logo circuit" className="w-10 h-10 object-contain" />
                                )}
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    {event.circuit?.acronym && (
                                      <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                        {event.circuit.acronym}
                                      </span>
                                    )}
                                  </div>
                                  <h4 className="font-bold text-lg text-gray-800">{event.title}</h4>
                                  <p className="text-sm text-gray-600">{formatEventPeriod(event)}</p>
                                  <p className="text-xs text-gray-500 mt-1">{event.description}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {event.participating && (
                                  <span className="bg-green-500 text-white text-xs font-semibold px-2 py-1 rounded-full">
                                    ✓ Inscrit
                                  </span>
                                )}
                                <div className={`w-3 h-3 rounded-full ${event.circuit?.color || 'bg-blue-600'}`}></div>
                              </div>
                            </div>
                          </div>

                          <div className="p-4">
                            <p className="text-xs font-semibold text-gray-600 mb-3">
                              {event.subEvents.length} séance{event.subEvents.length > 1 ? 's' : ''} :
                            </p>
                            <div className="space-y-3">
                              {event.subEvents.map(subEvent => (
                                <div
                                  key={subEvent.id}
                                  onClick={() => handleSubEventClick({ ...subEvent, parentEvent: event })}
                                  className={`bg-gray-50 rounded-lg p-3 border-l-4 transition cursor-pointer hover:shadow-md ${
                                    subEvent.participating ? 'border-green-500 bg-green-50 hover:bg-green-100' : 'border-gray-300 hover:bg-gray-100'
                                  }`}
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className={`text-xs px-2 py-0.5 rounded ${event.color} text-white font-semibold`}>
                                          {new Date(subEvent.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} - {subEvent.time}
                                        </span>
                                      </div>
                                      <h5 className="font-semibold text-gray-800">{subEvent.title}</h5>
                                      <p className="text-xs text-gray-600 mt-1">{subEvent.description}</p>
                                      {subEvent.detailedInfo?.location && (
                                        <p className="text-xs text-gray-500 mt-1">📍 {subEvent.detailedInfo.location}</p>
                                      )}
                                    </div>
                                    <div className="ml-2 flex-shrink-0">
                                      {subEvent.participating ? (
                                        <span className="bg-green-500 text-white text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap">
                                          ✓ Inscrit
                                        </span>
                                      ) : (
                                        <span className="bg-gray-300 text-gray-700 text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap">
                                          Absent
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    {getEventsForMonth(filteredEvents, currentDate.getFullYear(), currentDate.getMonth()).length === 0 && (
                      <p className="text-gray-500 text-center py-8">Aucun événement ce mois avec les filtres actifs</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarPage;